'use client';

/**
 * Type declaration for the Google global site tag function.
 */
type GtagFunction = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
  }
}

/**
 * Returns the gtag function if the Google tag has been loaded, or null.
 */
function getGtag(): GtagFunction | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (typeof window.gtag !== 'function') {
    return null;
  }

  return window.gtag;
}

/**
 * Initializes the Google Ads gtag.js script for conversion tracking.
 *
 * Injects the gtag.js script tag and configures the conversion ID.
 * Guards against double-init by checking if the script already exists.
 * No-ops if conversionId is empty/undefined or if running server-side.
 */
export function initGoogleAds(conversionId: string | undefined): void {
  if (!conversionId) {
    return;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Guard against double-init: check if script already loaded
  const existingScript = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js?id=${conversionId}"]`,
  );

  if (existingScript) {
    return;
  }

  // Inject gtag.js script
  const script = document.createElement('script');

  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer ?? [];

  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', conversionId, { send_page_view: false });
}

/**
 * Fires a Google Ads conversion event.
 *
 * @param conversionId  - The Google Ads conversion ID (e.g. "AW-123456789")
 * @param conversionLabel - The conversion label (e.g. "abcDEF123")
 * @param value - Optional monetary value of the conversion
 */
export function trackGoogleConversion(
  conversionId: string,
  conversionLabel: string,
  value?: number,
): void {
  const gtag = getGtag();

  if (!gtag) return;

  const eventParams: Record<string, unknown> = {
    send_to: `${conversionId}/${conversionLabel}`,
  };

  if (value !== undefined) {
    eventParams.value = value;
    eventParams.currency = 'USD';
  }

  gtag('event', 'conversion', eventParams);
}
